export type LobbyStatus = 'WAITING' | 'STARTING' | 'IN_PROGRESS' | 'FINISHED' | 'CANCELLED';
export type GameSessionStatus = 'ACTIVE' | 'FINISHED' | 'TIMEOUT' | 'CANCELLED';
export type LobbyPlayerStatus = 'WAITING' | 'READY' | 'PLAYING' | 'LEFT' | 'KICKED';

export interface LobbyWithStats {
  id: number;
  gameId: number;
  gameName: string;
  gameType: string;
  hostUserId: number;
  hostUsername: string | null;
  hostFirstName: string | null;
  name: string | null;
  isPrivate: boolean;
  language: string;
  status: LobbyStatus;
  maxPlayers: number;
  currentPlayers: number;
  createdAt: string;
  expiresAt: string | null;
}

export interface LobbyPlayer {
  id: number;
  lobbyId: number;
  userId: number;
  status: LobbyPlayerStatus;
  isReady: boolean;
  turnOrder: number | null;
  score: number;
  joinedAt: string;
  leftAt: string | null;
  kickedByAdminId: number | null;
  kickReason: string | null;
  user: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
  };
}

export interface LobbyDetails {
  id: number;
  gameId: number;
  game: {
    id: number;
    type: string;
    name: string;
  };
  host: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
  };
  name: string | null;
  isPrivate: boolean;
  language: string;
  status: LobbyStatus;
  maxPlayers: number;
  settings: Record<string, unknown> | null;
  chatRoomId: number | null;
  expiresAt: string | null;
  createdAt: string;
  players: LobbyPlayer[];
  activeSession: {
    id: number;
    status: string;
    startedAt: string;
    finishedAt: string | null;
  } | null;
}

export interface SessionWithStats {
  id: number;
  lobbyId: number | null;
  gameId: number;
  gameName: string;
  gameType: string;
  status: GameSessionStatus;
  turnNumber: number;
  winnerId: number | null;
  startedAt: string;
  finishedAt: string | null;
  movesCount: number;
}

export interface SessionDetails {
  id: number;
  lobbyId: number | null;
  gameId: number;
  game: {
    id: number;
    type: string;
    name: string;
  };
  status: GameSessionStatus;
  gameState: Record<string, unknown>;
  currentTurnUserId: number | null;
  turnNumber: number;
  winnerId: number | null;
  winner: {
    id: number;
    firstName: string | null;
    username: string | null;
  } | null;
  startedAt: string;
  finishedAt: string | null;
  players: Array<{
    userId: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    score: number;
  }>;
}

export interface GameMove {
  id: number;
  sessionId: number;
  userId: number;
  moveType: string;
  moveData: Record<string, unknown>;
  result: Record<string, unknown> | null;
  createdAt: string;
  user?: {
    id: number;
    firstName: string | null;
    username: string | null;
  };
}

export const LOBBY_STATUS_LABELS: Record<LobbyStatus, string> = {
  WAITING: 'Ожидание',
  STARTING: 'Запуск',
  IN_PROGRESS: 'В игре',
  FINISHED: 'Завершено',
  CANCELLED: 'Отменено',
};

export const LOBBY_STATUS_COLORS: Record<LobbyStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  WAITING: 'default',
  STARTING: 'secondary',
  IN_PROGRESS: 'default',
  FINISHED: 'secondary',
  CANCELLED: 'destructive',
};

export const SESSION_STATUS_LABELS: Record<GameSessionStatus, string> = {
  ACTIVE: 'Активна',
  FINISHED: 'Завершена',
  TIMEOUT: 'Тайм-аут',
  CANCELLED: 'Отменена',
};

export const SESSION_STATUS_COLORS: Record<GameSessionStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  ACTIVE: 'default',
  FINISHED: 'secondary',
  TIMEOUT: 'outline',
  CANCELLED: 'destructive',
};

export const PLAYER_STATUS_LABELS: Record<LobbyPlayerStatus, string> = {
  WAITING: 'Ожидает',
  READY: 'Готов',
  PLAYING: 'Играет',
  LEFT: 'Покинул',
  KICKED: 'Исключён',
};

export const PLAYER_STATUS_COLORS: Record<LobbyPlayerStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  WAITING: 'outline',
  READY: 'default',
  PLAYING: 'default',
  LEFT: 'secondary',
  KICKED: 'destructive',
};

export const GAME_TYPE_LABELS: Record<string, string> = {
  CARD_COLOR: 'Угадай цвет',
  GUESS_NUMBER: 'Угадай число',
};
